import { getCultivarQueryOptions } from "@packages/api-client/hooks/cultivarController";
import type { UpdateCultivarMutationRequest } from "@packages/api-client/types/cultivarController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUpdateCultivar } from "@/api/grass";
import { LoadFailedState } from "@/components/LoadFailedState";
import { PageHeader } from "@/components/PageHeader";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { CultivarForm } from "../-components/CultivarForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/cultivars/$cultivarId/edit",
)({
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	validateSearch: (search: Record<string, unknown>): { farm?: string } => ({
		farm: typeof search.farm === "string" ? search.farm : undefined,
	}),
	component: EditCultivarPage,
});

function EditCultivarPage() {
	const { orgId, cultivarId } = Route.useParams();
	const { farm } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const cultivar = useQuery(getCultivarQueryOptions(orgId, cultivarId));
	const updateMutation = useUpdateCultivar();

	const handleSubmit = (data: UpdateCultivarMutationRequest) =>
		updateMutation
			.mutateAsync({ organizationId: orgId, cultivarId, data })
			.then(() => {
				void navigate({
					to: "/app/org/$orgId/grass-production/cultivars",
					params: { orgId },
					search: { farm },
				});
			});

	if (cultivar.isPending) return <DashboardSkeleton />;
	if (cultivar.isError || !cultivar.data) {
		return <LoadFailedState onRetry={() => void cultivar.refetch()} />;
	}
	if (!cultivar.data.organizationId) {
		return (
			<div className="flex flex-col gap-6">
				<PageHeader title={t("grass.cultivar.edit.title")} showBackButton />
				<p className="text-sm text-base-content/70">
					{t("grass.cultivar.list.systemDialog.description")}
				</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title={t("grass.cultivar.edit.title")} showBackButton />
			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					<CultivarForm
						cultivar={cultivar.data}
						onSubmit={handleSubmit}
						isSubmitting={updateMutation.isPending}
					/>
				</div>
			</div>
		</div>
	);
}
